import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useWebSocketCall } from '../../../hooks/use-websocket-call';
import { cn } from '../../../utils';
import type { QCallFloatingConfig } from '../../../types';
import { StyleContainer } from '../shared';
import { DefaultFloatingButton } from './default-floating-button';
import { DefaultWidgetPanel } from './default-widget-panel';
import { MicIcon, MicOffIcon, LoaderIcon, BotIcon, PhoneOffIcon, ChatIcon } from '../../shared/icons';

export interface DefaultStyleContainerProps {
  config: QCallFloatingConfig;
  className?: string;
}

type ViewMode = 'call' | 'transcript';

/**
 * Default style container - ElevenLabs + Intercom inspired design:
 * - Coral (#FF6B6B) accent
 * - Off-white (#F8F9FA) surface
 * - Blue gradient avatar
 * - Rounded 16px geometry
 * - Intercom-style chat bubbles in transcript view
 */
export function DefaultStyleContainer({ config, className }: DefaultStyleContainerProps) {
  const [isOpen, setIsOpen] = useState(config.autoOpen ?? false);
  const [callDuration, setCallDuration] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('call');
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const {
    position = 'bottom-right',
    pulse = true,
    offset,
    avatar,
    agentName = 'AI Assistant',
    onConnect,
    onDisconnect,
    onError,
    assistantId,
    apiKey,
    userId,
    workspaceId,
    apiBaseUrl,
    wsUrl,
    voiceModal,
    voiceName,
  } = config;

  const {
    connect,
    disconnect,
    connectionState,
    isAudioMuted,
    toggleAudio,
    error,
    transcript,
  } = useWebSocketCall({
    wsUrl,
    assistantId: assistantId || '',
    apiKey,
    userId,
    workspaceId,
    apiBaseUrl,
    voiceModal,
    voiceName,
    onConnect,
    onDisconnect,
    onError,
  });

  // Timer effect
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (connectionState === 'connected') {
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(interval);
  }, [connectionState]);

  // Auto-scroll transcript
  useEffect(() => {
    if (chatContainerRef.current && viewMode === 'transcript') {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [transcript, viewMode]);

  // Reset view mode on disconnect
  useEffect(() => {
    if (connectionState === 'idle' || connectionState === 'disconnected') {
      setViewMode('call');
    }
  }, [connectionState]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggleMute = useCallback(() => {
    toggleAudio();
  }, [toggleAudio]);

  const handleEndCall = useCallback(() => {
    disconnect();
    setCallDuration(0);
    setViewMode('call');
    setIsOpen(false);
  }, [disconnect]);

  const handleStartCall = useCallback(async () => {
    if (!assistantId) {
      console.error('assistantId is required');
      return;
    }
    await connect();
  }, [connect, assistantId]);

  const handleButtonClick = useCallback(() => {
    if (connectionState === 'idle') {
      handleStartCall();
      setIsOpen(true);
    } else {
      handleToggle();
    }
  }, [connectionState, handleStartCall, handleToggle]);

  const isConnected = connectionState === 'connected';
  const isConnecting = connectionState === 'connecting';

  return (
    <StyleContainer uiStyle="default" className={className}>
      <DefaultFloatingButton
        onClick={handleButtonClick}
        position={position}
        avatar={avatar}
        agentName={agentName}
        pulse={pulse && connectionState === 'idle'}
        status={connectionState}
        timerText={formatTime(callDuration)}
        offset={offset}
      />

      <DefaultWidgetPanel
        isOpen={isOpen}
        onClose={handleClose}
        position={position}
      >
        {(isConnected || isConnecting) ? (
          <div className="flex flex-col h-full">
            {viewMode === 'call' ? (
              /* Call view - ElevenLabs style */
              <div className="flex-1 flex flex-col items-center justify-center px-8">
                {/* Timer */}
                <div
                  className="mb-4"
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    color: '#3f3f3f',
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                  }}
                >
                  {isConnected ? formatTime(callDuration) : 'Connecting'}
                </div>

                {/* Avatar - Blue gradient sphere */}
                <div
                  className={cn(
                    "relative flex items-center justify-center rounded-2xl shrink-0 overflow-hidden w-[80px] h-[80px]",
                    isConnecting && "animate-pulse",
                  )}
                  style={{
                    background: 'linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)',
                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.30)',
                  }}
                >
                  {avatar ? (
                    <img src={avatar} alt={agentName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BotIcon className="w-9 h-9 text-white" />
                    </div>
                  )}
                  {/* Glow ring during call */}
                  {isConnected && (
                    <div
                      className="absolute inset-0 rounded-2xl"
                      style={{
                        animation: 'qcall-avatar-glow 2s ease-in-out infinite',
                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.40)',
                      }}
                    />
                  )}
                </div>

                {/* Agent name */}
                <h3
                  className="mt-4"
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    letterSpacing: '-0.36px',
                    color: '#111111',
                    lineHeight: 1,
                  }}
                >
                  {agentName}
                </h3>
                <p
                  className="mt-1"
                  style={{
                    fontSize: '13px',
                    color: '#7b7b78',
                    letterSpacing: '0.2px',
                    lineHeight: 1,
                  }}
                >
                  {isConnecting ? 'Connecting...' : 'Listening...'}
                </p>

                {/* Two circular control buttons */}
                <div className="flex items-center gap-4 mt-8">
                  {/* Mic toggle */}
                  <button
                    className={cn(
                      "flex items-center justify-center w-12 h-12 rounded-full",
                      "transition-all duration-150 hover:scale-110 active:scale-90",
                      "bg-white border border-[#e5e7eb]"
                    )}
                    style={{
                      color: isAudioMuted ? '#c41c1c' : '#313130',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    }}
                    onClick={handleToggleMute}
                    aria-label={isAudioMuted ? "Unmute" : "Mute"}
                    disabled={!isConnected}
                  >
                    {isAudioMuted
                      ? <MicOffIcon className="w-5 h-5" />
                      : <MicIcon className="w-5 h-5" />
                    }
                  </button>

                  {/* End call - Coral */}
                  <button
                    className={cn(
                      "flex items-center justify-center w-12 h-12 rounded-full",
                      "transition-all duration-150 hover:scale-110 active:scale-90"
                    )}
                    style={{
                      backgroundColor: '#FF6B6B',
                      color: '#ffffff',
                      boxShadow: '0 4px 14px rgba(255, 107, 107, 0.40)',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = '#FF5252';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = '#FF6B6B';
                    }}
                    onClick={handleEndCall}
                    aria-label="End call"
                    disabled={!isConnected && !isConnecting}
                  >
                    {isConnecting ? (
                      <LoaderIcon className="w-5 h-5 animate-spin" />
                    ) : (
                      <PhoneOffIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {/* Chat icon with badge */}
                <button
                  className="relative mt-6 p-2 rounded-xl transition-all duration-150 hover:scale-110"
                  style={{ color: '#7b7b78' }}
                  onClick={() => setViewMode('transcript')}
                  aria-label="Open transcript"
                >
                  <ChatIcon className="w-5 h-5" />
                  {transcript.length > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-white rounded-full flex items-center justify-center leading-none"
                      style={{
                        backgroundColor: '#FF6B6B',
                        fontSize: '10px',
                        fontWeight: 600,
                      }}
                    >
                      {transcript.length > 9 ? '9+' : transcript.length}
                    </span>
                  )}
                </button>
              </div>
            ) : (
              /* Transcript view - Intercom-style */
              <div className="flex-1 min-h-0 flex flex-col">
                {/* Header with avatar, name, timer, and back button */}
                <div className="flex-shrink-0 flex items-center gap-3 px-5 pt-5 pb-3">
                  <button
                    className="p-1.5 rounded-lg transition-all duration-150 hover:scale-110"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.06)', color: '#313130' }}
                    onClick={() => setViewMode('call')}
                    aria-label="Back to call view"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4"
                    >
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>

                  <div
                    className="w-8 h-8 rounded-xl overflow-hidden shrink-0 flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)' }}
                  >
                    {avatar ? (
                      <img src={avatar} alt={agentName} className="w-full h-full object-cover" />
                    ) : (
                      <BotIcon className="w-4 h-4 text-white" />
                    )}
                  </div>

                  <div className="flex flex-col">
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        letterSpacing: '-0.16px',
                        color: '#111111',
                        lineHeight: 1.2,
                      }}
                    >
                      {agentName}
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        color: '#7b7b78',
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: 1.2,
                      }}
                    >
                      {isConnected ? formatTime(callDuration) : '00:00'}
                    </span>
                  </div>
                </div>

                {/* Transcript messages - Intercom-style rounded bubbles */}
                <div className="flex-1 min-h-0 flex flex-col px-4 pb-4 overflow-hidden">
                  {transcript.length > 0 ? (
                    <div
                      ref={chatContainerRef}
                      className="flex-1 overflow-y-auto qcall-scrollbar space-y-3"
                    >
                      {transcript.map((msg) => (
                        <div
                          key={msg.id}
                          className={cn(
                            "flex",
                            msg.speaker === 'user' ? "justify-end" : "justify-start"
                          )}
                        >
                          <div
                            className={cn("max-w-[80%]")}
                            style={{
                              padding: '10px 14px',
                              borderRadius: '20px',
                              fontSize: '14px',
                              lineHeight: '1.5',
                              letterSpacing: '-0.08px',
                              ...(msg.speaker === 'user'
                                ? {
                                    backgroundColor: '#333333',
                                    color: '#ffffff',
                                    borderBottomRightRadius: '6px',
                                  }
                                : {
                                    backgroundColor: '#F0F0F0',
                                    color: '#111111',
                                    borderBottomLeftRadius: '6px',
                                  }
                              ),
                            }}
                          >
                            {msg.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#7b7b78',
                        }}
                      >
                        Conversation will appear here
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex-shrink-0 px-4 py-1">
                <p
                  className="text-center"
                  style={{ color: '#c41c1c', fontSize: '12px' }}
                >
                  {error.message}
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Idle State - ElevenLabs style landing */
          <div className="flex flex-col items-center justify-center h-full pt-10 pb-8 px-8 text-center">
            {/* Large avatar - Blue gradient sphere */}
            <div
              className="relative flex items-center justify-center rounded-[22px] shrink-0 overflow-hidden w-[88px] h-[88px]"
              style={{
                background: 'linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)',
                boxShadow: '0 6px 24px rgba(59, 130, 246, 0.35)',
              }}
            >
              {avatar ? (
                <img src={avatar} alt={agentName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <BotIcon className="w-10 h-10 text-white" />
                </div>
              )}
            </div>

            {/* Agent name */}
            <h3
              className="mt-5"
              style={{
                fontSize: '20px',
                fontWeight: 600,
                letterSpacing: '-0.40px',
                color: '#111111',
                lineHeight: 1,
              }}
            >
              {agentName}
            </h3>
            <p
              className="mt-1.5"
              style={{
                fontSize: '13px',
                color: '#7b7b78',
                letterSpacing: '-0.08px',
                lineHeight: 1.4,
              }}
            >
              Your AI voice assistant
            </p>

            {/* Start call button */}
            <button
              className={cn(
                "flex items-center justify-center gap-2 mt-7",
                "transition-all duration-200 ease-out",
                "hover:scale-105 active:scale-95",
              )}
              style={{
                padding: '12px 28px',
                backgroundColor: '#111111',
                color: '#ffffff',
                borderRadius: '14px',
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '-0.08px',
                lineHeight: 1.5,
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.backgroundColor = '#333333';
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.backgroundColor = '#111111';
              }}
              onClick={handleStartCall}
            >
              <MicIcon className="w-4 h-4" />
              Start call
            </button>

            <p
              className="mt-6 max-w-[260px]"
              style={{
                fontSize: '12px',
                color: '#7b7b78',
                letterSpacing: '0.2px',
                lineHeight: 1.5,
              }}
            >
              Discover the capabilities of conversational voice AI
            </p>

            {/* Mic blocked error - shown in idle state after failed attempt */}
            {error && (
              <div
                className="mt-4 w-full max-w-[280px] p-3 rounded-xl text-left"
                style={{
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                }}
              >
                <div className="flex items-start gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <p style={{ fontSize: '12px', color: '#991B1B', lineHeight: 1.5 }}>
                    {error.message}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </DefaultWidgetPanel>
    </StyleContainer>
  );
}
